import type { VariantProps } from 'class-variance-authority';
import type { StaticImageData } from 'next/image';
import { cva } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import activeCoin from '@/public/assets/image/gamification/daily-streak/streak-coin-active.png';
import completedCoin from '@/public/assets/image/gamification/daily-streak/streak-coin-claimed.png';
import defaultCoin from '@/public/assets/image/gamification/daily-streak/streak-coin-default.png';

import { cn } from '@/utils/cn';
import { formatCompactNumber } from '@/utils/currency.utils';

const cardVariants = cva('gm-daily-streak-card relative z-10 flex size-full shrink-0 flex-col items-center justify-end overflow-visible rounded-[inherit] p-2 text-center', {
  variants: {
    variant: {
      default: 'default',
      active: 'active',
      completed: 'completed',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const cardContentVariants = cva('relative z-1 flex w-full shrink-0 flex-col items-start text-center', {
  variants: {
    variant: {
      default: 'text-c500',
      active: 'gm-text-gold-gradient',
      completed: 'text-c500 opacity-50',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const cardCoinTextVariants = cva('-mt-0.5 text-sm md:-mt-1.25 md:text-2xl font-semibold md:font-medium z-20 absolute top-1/2 -translate-y-1/2 left-1/2 transform -translate-x-1/2', {
  variants: {
    variant: {
      default: 'text-c300 streak-text-shadow',
      active: 'coin-text text-white',
      completed: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});
export type GmDailyStreakCardVariant = VariantProps<typeof cardVariants>;

const coinImage: Record<NonNullable<GmDailyStreakCardVariant['variant']>, StaticImageData> = {
  active: activeCoin,
  default: defaultCoin,
  completed: completedCoin,
};

interface GmDailyStreakCardProps extends GmDailyStreakCardVariant {
  xp: number;
  day: number;
}

export const GmDailyStreakCard = ({ variant, xp, day }: GmDailyStreakCardProps) => {
  const t = useTranslations('gamification.daily-streak');

  const coinSrc = coinImage[variant ?? 'default'];
  const isSmallCoinSize = variant === 'default' || variant === 'active';

  return (
    <div className="gm-daily-streak-card-wrapper h-16.5 w-14 rounded-[0.5rem] md:h-29.5 md:w-25 md:rounded-[0.893rem]">
      <div
        data-node="streak-card"
        data-variant={variant}
        aria-label={t('day-label', { day, xp })}
        className={cn(cardVariants({ variant }))}
      >

        <div className={cn('absolute md:-top-6 size-12.5', isSmallCoinSize ? 'md:size-22 -top-4.25' : '-top-3.75 md:size-24')}>
          <Image
            fill
            src={coinSrc}
            alt="Reward coin"
          />
          {variant !== 'completed' && (
            <div className={cn(cardCoinTextVariants({ variant }))}>{day}</div>
          )}
        </div>

        <div className={cn(cardContentVariants({ variant }))}>
          <p className="w-full text-sm leading-5 md:text-2xl md:font-medium font-semibold md:leading-7.25">
            {formatCompactNumber(xp, { minUnit: 'thousand' })}
          </p>
          <p className="flex w-full flex-col justify-center text-[9px] leading-none md:text-base md:leading-normal">
            {t('xp')}
          </p>
        </div>
      </div>

    </div>

  );
};
